/**
 * ComfyUISDOptions - Configuration panel for ComfyUI SD 1.5 AI image generation.
 * Mirrors FooocusStyleOptions structure and dark-theme styling conventions.
 */

import { useEffect, useState } from 'react';
import { Cpu, Layers, Sliders } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { StyleSelector } from '../../image/StyleSelector';
import { ImageModelSelector } from '../../image/ImageModelSelector';
import { listInstalledLoras } from '@/services/loraApi';
import type { LoraInfo } from '@/services/loraApi';

// Step option presets (matches backend constants COMFYUI_SD_HYPER_STEP_OPTIONS +
// COMFYUI_SD_STANDARD_STEP_OPTIONS).  We keep a static list here so we avoid a
// runtime JSON import from shared_constants.json (which is large and awkward).
const HYPER_STEPS: number[] = [4, 6, 8];
const STANDARD_STEPS: number[] = [20, 25, 30];
const ALL_STEP_OPTIONS: number[] = [...HYPER_STEPS, ...STANDARD_STEPS];
const DEFAULT_STEPS = 6; // COMFYUI_SD_HYPER_RECOMMENDED_STEPS

// Maximum number of styles allowed (same cap as Fooocus so the shared backend
// field "fooocus_styles" is not over-filled).
const MAX_STYLES = 10;

export interface LoraEntry {
  filename: string;
  strength: number;
}

interface ComfyUISDOptionsProps {
  model?: string;
  steps?: number;
  loras?: LoraEntry[];
  /** Styles — written to the shared fooocus_styles field (backend applies them as prompt prefixes). */
  styles: string[];
  onModelChange: (model: string) => void;
  onStepsChange: (steps: number) => void;
  onLorasChange: (loras: LoraEntry[]) => void;
  onStylesChange: (styles: string[]) => void;
  disabled: boolean;
  /**
   * Whether to render the style multi-select. Driven by
   * `PROVIDER_FEATURES['comfyui-sd'].supportsStyles` at the call site so this
   * subpanel matches the model-aware gating used by ImageConfigForm /
   * ImageGeneratorPanel. Defaults to true (ComfyUI-SD honors fooocus_styles).
   */
  showStyles?: boolean;
  /**
   * Whether to render the checkpoint model selector. Driven by
   * `PROVIDER_FEATURES['comfyui-sd'].supportsModels`. Defaults to true.
   */
  showModel?: boolean;
}

export function ComfyUISDOptions({
  model,
  steps,
  loras = [],
  styles,
  onModelChange,
  onStepsChange,
  onLorasChange,
  onStylesChange,
  disabled,
  showStyles = true,
  showModel = true,
}: ComfyUISDOptionsProps) {
  const { t } = useTranslation();

  // LoRA state
  const [availableLoras, setAvailableLoras] = useState<LoraInfo[]>([]);
  const [lorasLoading, setLorasLoading] = useState(true);

  // Load installed SD 1.5 LoRAs on mount
  useEffect(() => {
    let cancelled = false;
    setLorasLoading(true);
    listInstalledLoras()
      .then((all) => {
        if (!cancelled) {
          setAvailableLoras(all.filter((l) => l.base_model_compat === 'SD 1.5' && l.is_installed));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableLoras([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLorasLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Toggle a LoRA on/off
  const handleLoraToggle = (lora: LoraInfo) => {
    if (disabled) return;
    const alreadyEnabled = loras.some((l) => l.filename === lora.filename);
    if (alreadyEnabled) {
      onLorasChange(loras.filter((l) => l.filename !== lora.filename));
    } else {
      onLorasChange([...loras, { filename: lora.filename, strength: lora.recommended_strength }]);
    }
  };

  // Update strength for an enabled LoRA
  const handleStrengthChange = (filename: string, strength: number) => {
    if (disabled) return;
    onLorasChange(loras.map((l) => (l.filename === filename ? { ...l, strength } : l)));
  };

  const effectiveSteps = steps ?? DEFAULT_STEPS;

  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-4 bg-black/20">
      {/* Header */}
      <h4 className="text-sm font-medium text-white/80 flex items-center gap-2">
        <Cpu className="h-4 w-4 text-primary-400 flex-shrink-0" />
        <span className="break-words">
          {t('comfyuiSd.title', { defaultValue: 'ComfyUI SD 1.5 Settings' })}
        </span>
      </h4>

      {/* Checkpoint Model Selector */}
      {showModel && (
        <ImageModelSelector
          value={model}
          onChange={onModelChange}
          provider="comfyui-sd"
          disabled={disabled}
        />
      )}

      {/* Steps Selector */}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <label className="text-sm font-medium text-white/70 flex items-center gap-1">
            <Sliders className="h-4 w-4 flex-shrink-0 text-primary-400" />
            {t('comfyuiSd.steps.label', { defaultValue: 'Sampler Steps' })}
          </label>
          <span className="text-xs text-white/40">
            {t('comfyuiSd.steps.hint', {
              defaultValue: '4-8 = Hyper-speed (recommended). 20-30 = Standard quality.',
            })}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {ALL_STEP_OPTIONS.map((option) => {
            const isHyper = HYPER_STEPS.includes(option);
            const active = effectiveSteps === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => !disabled && onStepsChange(option)}
                disabled={disabled}
                title={isHyper ? 'Hyper-speed (requires Hyper LoRA)' : 'Standard quality'}
                className={[
                  'min-h-[44px] min-w-[44px] px-3 py-2 text-xs rounded-lg transition-colors',
                  active
                    ? 'bg-primary-600 text-white border border-primary-500 shadow-sm'
                    : 'bg-white/5 text-white/60 hover:bg-primary-900/30 hover:text-primary-300 border border-white/10',
                  disabled ? 'opacity-50 cursor-not-allowed' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {option}
                {isHyper && (
                  <span className="ml-1 text-[10px] text-primary-300 font-semibold">H</span>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-white/40">
          {t('comfyuiSd.steps.hyperNote', {
            defaultValue:
              '"H" = Hyper-speed steps. Requires a Hyper LoRA to be enabled below for best results.',
          })}
        </p>
      </div>

      {/* Style Selector */}
      {showStyles && (
        <div className="space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <label className="text-sm font-medium text-white/70">
              {t('comfyuiSd.styles.label', { defaultValue: 'Style Presets' })}
            </label>
            <span className="text-xs text-white/40">
              {t('comfyuiSd.styles.hint', {
                defaultValue: 'Styles are added as prompt prefixes before generation.',
              })}
            </span>
          </div>
          <StyleSelector
            selectedStyles={styles}
            onChange={onStylesChange}
            maxStyles={MAX_STYLES}
            disabled={disabled}
            provider="comfyui-sd"
          />
        </div>
      )}

      {/* LoRA Picker */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-white/70 flex items-center gap-1">
          <Layers className="h-4 w-4 flex-shrink-0 text-primary-400" />
          {t('comfyuiSd.loras.label', { defaultValue: 'SD 1.5 LoRAs' })}
        </label>

        {lorasLoading && (
          <p className="text-xs text-white/40">
            {t('comfyuiSd.loras.loading', { defaultValue: 'Loading installed LoRAs...' })}
          </p>
        )}

        {!lorasLoading && availableLoras.length === 0 && (
          <p className="text-xs text-white/40">
            {t('comfyuiSd.loras.empty', {
              defaultValue: 'No SD 1.5 LoRAs installed — add them in Manage Models.',
            })}
          </p>
        )}

        {!lorasLoading && availableLoras.length > 0 && (
          <div className="space-y-3">
            {/* Toggle chips */}
            <div className="flex flex-wrap gap-1">
              {availableLoras.map((lora) => {
                const enabled = loras.some((l) => l.filename === lora.filename);
                return (
                  <button
                    key={lora.filename}
                    type="button"
                    onClick={() => handleLoraToggle(lora)}
                    disabled={disabled}
                    title={lora.description || lora.display_name}
                    className={[
                      'min-h-[44px] px-3 py-1 text-xs rounded-full transition-colors',
                      enabled
                        ? 'bg-primary-600 text-white border border-primary-500 shadow-sm'
                        : 'bg-white/5 text-white/60 hover:bg-primary-900/30 hover:text-primary-300 border border-white/10',
                      disabled ? 'opacity-50 cursor-not-allowed' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {lora.display_name}
                    {lora.is_hyper_speed_lora && (
                      <span className="ml-1 text-[10px] text-primary-300 font-semibold">H</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Strength sliders for enabled LoRAs */}
            {loras.length > 0 && (
              <div className="space-y-2">
                {loras.map((entry) => {
                  const loraInfo = availableLoras.find((l) => l.filename === entry.filename);
                  return (
                    <div
                      key={entry.filename}
                      className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 rounded-lg bg-black/30 border border-white/5"
                    >
                      <span className="text-xs text-white/70 flex-1 min-w-0 truncate">
                        {loraInfo?.display_name ?? entry.filename}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-white/40 w-10 text-right tabular-nums">
                          {entry.strength.toFixed(2)}
                        </span>
                        <input
                          type="range"
                          min={-1.5}
                          max={1.5}
                          step={0.05}
                          value={entry.strength}
                          disabled={disabled}
                          onChange={(e) =>
                            handleStrengthChange(entry.filename, parseFloat(e.target.value))
                          }
                          className={[
                            'w-24 accent-primary-500',
                            disabled ? 'opacity-50 cursor-not-allowed' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-label={t('comfyuiSd.loras.strengthAriaLabel', {
                            defaultValue: 'LoRA strength',
                          })}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
